import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  Field,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useRxRequestAccess } from '@/features/prescription-requests/access';
import {
  useApproveRxRequest,
  useBranchDoctors,
  usePrescriptionRequest,
  useRefuseRxRequest,
  useRouteRxRequest,
} from '@/features/prescription-requests/api';
import {
  RX_REQUEST_LIMITS,
  type RxAllergyBlock,
  type StaffRxRequest,
} from '@/features/prescription-requests/types';
import { isApiError } from '@/lib/api';
import {
  alertCount,
  EXCLUDED_REASON_LABEL,
  itemAlerts,
  itemDirections,
  KIND_LABEL,
  REVOKED_REASON_LABEL,
  ROUTE_REASON_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  when,
  WITHDRAWN_REASON_LABEL,
} from './rx-request-format';
import styles from './PrescriptionRequests.module.css';

/** Staff wording for the API's codes; the API's own message is shown under it. */
const ERRORS: Record<string, string> = {
  PRESCRIPTION_REQUEST_NOT_FOUND:
    'This request is no longer in your inbox. It may have been routed to another doctor.',
  PRESCRIPTION_REQUEST_NOT_PENDING:
    'This request was already decided or withdrawn. It has been reloaded.',
  PRESCRIPTION_REQUEST_CHANGED:
    'This request changed after you opened it. It has been reloaded: check it and try again.',
  PRESCRIPTION_REQUEST_NOT_ROUTED_TO_YOU:
    'This request is routed to another doctor. The clinic manager can route it to you.',
  APC_REQUIRED:
    'You need a current Annual Practising Certificate and MMC number on your profile to approve. Ask the Super admin to update it.',
  DOCTOR_NOT_AT_BRANCH: 'Choose an active doctor of the request’s branch.',
  PRESCRIPTION_CANCELLED:
    'The prescriber cancelled this prescription, so the request cannot be approved; its open requests were withdrawn with it.',
  REFILL_NOT_ALLOWED:
    'This medicine can no longer be repeated from the app. Refuse the request instead.',
  OUTSIDE_PRESCRIPTION_NOT_ALLOWED:
    'Nothing on this prescription can be written for an outside pharmacy any more. Refuse the request instead.',
  OUTSIDE_PRESCRIPTION_ALREADY_ISSUED:
    'A prescription for a pharmacy was already issued for this prescription.',
};

/** Codes after which the request on screen is out of date. */
const RELOAD = new Set([
  'PRESCRIPTION_REQUEST_CHANGED',
  'PRESCRIPTION_REQUEST_NOT_PENDING',
  'PRESCRIPTION_CANCELLED',
]);

interface Failure {
  title: string;
  detail?: string;
  requestId?: string;
}

type Mode = 'view' | 'refuse' | 'override';

/**
 * One refill / outside-pharmacy request (RL-1158): the medicines with their
 * allergy alerts and the patient's allergies (doctor), the decision (approve,
 * with an allergy override on 422 PRESCRIPTION_BLOCKED; refuse with a reason)
 * and routing (manager). Every action sends the `version` read; on 409
 * PRESCRIPTION_REQUEST_CHANGED the request is reloaded.
 */
export function PrescriptionRequestDrawer({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const q = usePrescriptionRequest(id);
  const r = q.data;
  const notFound =
    isApiError(q.error) && q.error.code === 'PRESCRIPTION_REQUEST_NOT_FOUND';

  return r ? (
    <RequestDialog
      r={r}
      reloading={q.isFetching}
      reload={() => void q.refetch()}
      onClose={onClose}
    />
  ) : (
    <Dialog open onClose={onClose} title="Prescription request">
      {q.isLoading ? (
        <LoadingState label="Loading the request" />
      ) : (
        <ErrorState
          error={q.error}
          title={notFound ? ERRORS.PRESCRIPTION_REQUEST_NOT_FOUND : undefined}
          onRetry={notFound ? undefined : () => void q.refetch()}
        />
      )}
    </Dialog>
  );
}

function RequestDialog({
  r,
  reloading,
  reload,
  onClose,
}: {
  r: StaffRxRequest;
  reloading: boolean;
  reload: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const formId = useId();
  const access = useRxRequestAccess();
  const approve = useApproveRxRequest(r.id);
  const refuse = useRefuseRxRequest(r.id);
  const [mode, setMode] = useState<Mode>('view');
  const [failure, setFailure] = useState<Failure | null>(null);
  const [block, setBlock] = useState<(RxAllergyBlock & Failure) | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string>();
  const pending = r.status === 'pending';
  const canDecide = access.canDecide && r.detailsShown && pending;
  const busy = approve.isPending || refuse.isPending;

  function fail(err: unknown) {
    if (!isApiError(err)) {
      setFailure({ title: 'Could not reach the server. Try again.' });
      return;
    }
    if (err.code === 'PRESCRIPTION_BLOCKED') {
      const p: Record<string, unknown> = err.problem ?? {};
      setBlock({
        title: 'Allergy on the patient record',
        detail: err.message,
        requestId: err.requestId,
        lines: Array.isArray(p.lines) ? (p.lines as string[]) : [],
        blockedItems: Array.isArray(p.blockedItems)
          ? (p.blockedItems as RxAllergyBlock['blockedItems'])
          : [],
      });
      setReason('');
      setReasonError(undefined);
      setMode('override');
      return;
    }
    const field =
      err.fieldError('reason') ?? err.fieldError('allergyOverride.reason');
    if (field) {
      setReasonError(field);
      return;
    }
    const known = ERRORS[err.code];
    setFailure({
      title: known ?? err.message,
      detail: known && err.message !== known ? err.message : undefined,
      requestId: err.requestId,
    });
    if (RELOAD.has(err.code)) {
      setMode('view');
      reload();
    }
  }

  async function doApprove(overrideReason?: string) {
    setFailure(null);
    try {
      const saved = await approve.mutateAsync({
        version: r.version,
        ...(overrideReason && { allergyOverride: { reason: overrideReason } }),
      });
      setMode('view');
      setBlock(null);
      toast.show({
        tone: 'success',
        title:
          saved.kind === 'outside_pharmacy'
            ? `Prescription for a pharmacy issued${saved.document ? `: ${saved.document.number}` : ''}`
            : 'Refill approved: the patient collects it at the counter',
      });
    } catch (err) {
      fail(err);
    }
  }

  async function doRefuse(text: string) {
    setFailure(null);
    try {
      await refuse.mutateAsync({ version: r.version, reason: text });
      setMode('view');
      toast.show({ tone: 'success', title: 'Request refused' });
    } catch (err) {
      fail(err);
    }
  }

  function submit() {
    const text = reason.trim();
    if (mode === 'refuse') {
      if (!text) return setReasonError('Enter a reason the patient will read.');
      void doRefuse(text);
    } else if (mode === 'override') {
      if (text.length < RX_REQUEST_LIMITS.overrideReasonMin)
        return setReasonError(
          'Enter why the medicine is safe for this patient (at least 2 characters).',
        );
      void doApprove(text);
    }
  }

  function start(next: Mode) {
    setMode(next);
    setReason('');
    setReasonError(undefined);
    setFailure(null);
  }

  const footer =
    mode === 'refuse' ? (
      <>
        <Button onClick={() => start('view')}>Cancel</Button>
        <Button
          type="submit"
          form={formId}
          variant="danger"
          loading={refuse.isPending}
        >
          Refuse request
        </Button>
      </>
    ) : mode === 'override' ? (
      <>
        <Button onClick={() => start('view')}>Cancel</Button>
        <Button
          type="submit"
          form={formId}
          variant="danger"
          loading={approve.isPending}
        >
          Approve with override
        </Button>
      </>
    ) : canDecide ? (
      <>
        <Button onClick={() => start('refuse')} disabled={busy}>
          Refuse
        </Button>
        <Button
          variant="primary"
          loading={approve.isPending}
          onClick={() => void doApprove()}
        >
          {r.kind === 'outside_pharmacy'
            ? 'Approve and issue'
            : 'Approve refill'}
        </Button>
      </>
    ) : (
      <Button onClick={onClose}>Close</Button>
    );

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={mode === 'view'}
      title={`${KIND_LABEL[r.kind]} · ${r.patient.fullName}`}
      footer={footer}
    >
      <div className={styles.drawer}>
        {failure && (
          <div className={styles.error} role="alert">
            {failure.title}
            {failure.detail && <span>{failure.detail}</span>}
            {failure.requestId && (
              <span>{t('common.requestId', { id: failure.requestId })}</span>
            )}
          </div>
        )}
        {reloading && <p className={styles.muted}>Reloading the request…</p>}

        <Summary r={r} />
        {r.document && <DocumentRow r={r} />}

        {!r.detailsShown ? (
          <p className={styles.oversightNote}>
            Oversight view: the medicines, the patient’s allergies and any
            refusal reason are shown only to the doctor who decides this
            request.
          </p>
        ) : (
          <>
            <AllergyAlerts r={r} />
            <Medicines r={r} />
            <Allergies r={r} />
          </>
        )}

        {mode !== 'view' && (
          <form
            id={formId}
            className={styles.form}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {mode === 'override' && block && (
              <div className={styles.blocked} role="alert">
                <strong>
                  <AlertTriangle size={16} aria-hidden="true" /> {block.title}
                </strong>
                {block.lines.length > 0 && (
                  <span>Blocked: {block.lines.join(', ')}</span>
                )}
                <span>{block.detail}</span>
                {block.requestId && (
                  <span>{t('common.requestId', { id: block.requestId })}</span>
                )}
              </div>
            )}
            <Field
              label={
                mode === 'refuse'
                  ? 'Reason for the patient'
                  : 'Reason to approve despite the allergy'
              }
              hint={
                mode === 'refuse'
                  ? 'The patient reads this in the app. It is never in the notification.'
                  : 'Kept on the request for staff only, e.g. how you checked it with the patient.'
              }
              error={reasonError}
            >
              <textarea
                className={styles.textarea}
                value={reason}
                maxLength={
                  mode === 'refuse'
                    ? RX_REQUEST_LIMITS.refuseReasonMax
                    : RX_REQUEST_LIMITS.overrideReasonMax
                }
                onChange={(e) => {
                  setReason(e.target.value);
                  setReasonError(undefined);
                }}
              />
            </Field>
          </form>
        )}

        {access.canRoute && pending && mode === 'view' && (
          <RouteForm r={r} onError={fail} clear={() => setFailure(null)} />
        )}
      </div>
    </Dialog>
  );
}

function Summary({ r }: { r: StaffRxRequest }) {
  return (
    <dl className={styles.kv}>
      <dt>Status</dt>
      <dd>
        <Badge tone={STATUS_TONE[r.status]} dot>
          {STATUS_LABEL[r.status]}
        </Badge>
      </dd>
      <dt>Prescription</dt>
      <dd>
        {r.prescription.number ?? '—'}
        {r.prescription.prescriber &&
          ` · ${r.prescription.prescriber.fullName}`}
      </dd>
      <dt>Branch</dt>
      <dd>{r.branch.name}</dd>
      <dt>Requested</dt>
      <dd>{when(r.createdAt)}</dd>
      <dt>Routed to</dt>
      <dd>
        {r.routedTo?.fullName ?? 'Branch doctors'}{' '}
        <span className={styles.muted}>
          ({ROUTE_REASON_LABEL[r.routeReason] ?? r.routeReason})
        </span>
      </dd>
      {r.refills && (
        <>
          <dt>Refills</dt>
          <dd>
            {r.refills.approvedBefore} of {r.refills.max} approved before
          </dd>
        </>
      )}
      {r.decidedAt && (
        <>
          <dt>Decided</dt>
          <dd>
            {when(r.decidedAt)}
            {r.decidedBy && ` · ${r.decidedBy.fullName}`}
          </dd>
        </>
      )}
      {r.refuseReason && (
        <>
          <dt>Refusal reason</dt>
          <dd>{r.refuseReason}</dd>
        </>
      )}
      {r.allergyOverrideReason && (
        <>
          <dt>Allergy override</dt>
          <dd>{r.allergyOverrideReason}</dd>
        </>
      )}
      {r.withdrawnAt && (
        <>
          <dt>Withdrawn</dt>
          <dd>
            {when(r.withdrawnAt)}
            {r.withdrawnReason &&
              ` · ${WITHDRAWN_REASON_LABEL[r.withdrawnReason]}`}
          </dd>
        </>
      )}
    </dl>
  );
}

function DocumentRow({ r }: { r: StaffRxRequest }) {
  const d = r.document!;
  return (
    <div className={styles.document} role="status">
      <span>
        Prescription for a pharmacy <strong className="mono">{d.number}</strong>
        {d.revokedAt && (
          <>
            {' '}
            <Badge tone="danger">Revoked</Badge>{' '}
            <span className={styles.muted}>
              {d.revokedReason
                ? REVOKED_REASON_LABEL[d.revokedReason]
                : 'no longer valid'}
            </span>
          </>
        )}
      </span>
      <span className={styles.muted}>
        The patient finds it in Documents in the app.{' '}
        <a
          href={`/verify/${encodeURIComponent(d.verificationCode)}`}
          target="_blank"
          rel="noreferrer"
        >
          Open the QR check <ExternalLink size={12} aria-hidden="true" />
          <span className="visually-hidden"> (opens in a new tab)</span>
        </a>
      </span>
    </div>
  );
}

function AllergyAlerts({ r }: { r: StaffRxRequest }) {
  const items = (r.items ?? []).filter((i) => i.allergyAlerts.length);
  if (!alertCount(r.items)) return null;
  return (
    <section className={styles.alerts} aria-label="Allergy alerts">
      <h3 className={styles.alertsTitle}>
        <AlertTriangle size={16} aria-hidden="true" /> Allergy alerts
      </h3>
      <ul className={styles.alertList}>
        {items.flatMap((i) =>
          itemAlerts(i, r.patientAllergies).map((a) => (
            <li key={`${i.itemId ?? i.medicine}:${a.text}`}>
              <strong>{i.medicine}</strong>
              {a.reported ? (
                <Badge tone="warning">Patient-reported, not confirmed</Badge>
              ) : (
                <Badge tone="danger">Confirmed allergy</Badge>
              )}
              <p>{a.text}</p>
            </li>
          )),
        )}
      </ul>
      {r.status === 'pending' && (
        <p className={styles.alertHelp}>
          Approving needs a reason (allergy override). You can also refuse, or
          confirm or reject the allergy on the{' '}
          <Link to={`/patients/${r.patient.id}`}>patient record</Link> first.
        </p>
      )}
    </section>
  );
}

function Medicines({ r }: { r: StaffRxRequest }) {
  const items = r.items ?? [];
  const excluded = r.excludedItems ?? [];
  return (
    <>
      <h3 className={styles.section}>Medicines</h3>
      {items.length ? (
        <ul className={styles.list}>
          {items.map((i) => (
            <li key={i.itemId ?? i.medicine}>
              <strong>{i.medicine}</strong>
              {i.allergyAlerts.length > 0 && (
                <Badge tone="danger">Allergy alert</Badge>
              )}
              {itemDirections(i) && <span>{itemDirections(i)}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.muted}>No medicines on this request.</p>
      )}
      {excluded.length > 0 && (
        <>
          <h3 className={styles.section}>
            {r.kind === 'outside_pharmacy'
              ? 'Left off the prescription for a pharmacy'
              : 'Not included'}
          </h3>
          <ul className={styles.list}>
            {excluded.map((x) => (
              <li key={x.itemId}>
                <strong>{x.medicine}</strong>
                <span>{EXCLUDED_REASON_LABEL[x.reason] ?? x.reason}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function Allergies({ r }: { r: StaffRxRequest }) {
  const list = r.patientAllergies ?? [];
  return (
    <>
      <h3 className={styles.section}>Patient allergies</h3>
      {list.length ? (
        <ul className={styles.list}>
          {list.map((a, i) => (
            <li key={`${a.substance}:${i}`}>
              <strong>
                {a.substance}
                {a.reaction && ` (${a.reaction.toLowerCase()})`}
              </strong>
              {a.status === 'reported' ? (
                <Badge tone="warning">Patient-reported, not confirmed</Badge>
              ) : (
                <Badge tone="neutral">Confirmed</Badge>
              )}
              {a.fromMergedRecord && (
                <span>
                  Recorded on a merged record
                  {a.fromRecord ? `: ${a.fromRecord.fullName}` : ''}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.muted}>No allergies on the record.</p>
      )}
    </>
  );
}

function RouteForm({
  r,
  onError,
  clear,
}: {
  r: StaffRxRequest;
  onError: (err: unknown) => void;
  clear: () => void;
}) {
  const toast = useToast();
  const doctors = useBranchDoctors(r.branch.id, true);
  const route = useRouteRxRequest(r.id);
  const current = r.routedTo?.id ?? '';
  const [doctorId, setDoctorId] = useState(current);
  const options = [
    { value: '', label: 'Any doctor of the branch' },
    ...(doctors.data ?? []).map((d) => ({ value: d.id, label: d.fullName })),
    // Keep the current doctor selectable even when not in the list.
    ...(r.routedTo && !doctors.data?.some((d) => d.id === r.routedTo!.id)
      ? [{ value: r.routedTo.id, label: r.routedTo.fullName }]
      : []),
  ];

  async function send() {
    clear();
    try {
      const saved = await route.mutateAsync({
        doctorId: doctorId || null,
        version: r.version,
      });
      toast.show({
        tone: 'success',
        title: saved.routedTo
          ? `Routed to ${saved.routedTo.fullName}`
          : 'Sent back to the branch’s doctors',
      });
    } catch (err) {
      onError(err);
    }
  }

  return (
    <form
      className={styles.route}
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <h3 className={styles.section}>Route</h3>
      <div className={styles.routeRow}>
        <Field
          label="Doctor"
          hint={
            doctors.isError
              ? 'Could not load the branch’s doctors.'
              : 'A doctor of the request’s branch decides it.'
          }
        >
          <Select
            value={doctorId}
            options={options}
            disabled={doctors.isLoading}
            onChange={(e) => setDoctorId(e.target.value)}
          />
        </Field>
        {/* RL-1828 (gap #151): Route only once another doctor is picked. */}
        {doctorId !== current && (
          <Button type="submit" loading={route.isPending}>
            Route
          </Button>
        )}
      </div>
    </form>
  );
}
